import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import GalleryCard from "./GalleryCard";
import GalleryViewPopup from "./GalleryViewPopup";
import { BASE_API_URL } from "../../config";
import { authFetch } from "../../Scripts/authHelper";

const CommunityPreview = (props: any) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [userVotes, setUserVotes] = useState<Record<number, string>>({});
  const [viewingCaptureId, setViewingCaptureId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await fetch(
          `${BASE_API_URL}/api/gallery?sort=popular&page=1&limit=4`
        );
        const data = await response.json();
        setItems(data.items || []);

        if (props.state.isAuthenticated && data.items?.length > 0) {
          const ids = data.items.map((item: any) => item.Id).join(",");
          const votesResponse = await authFetch(
            `${BASE_API_URL}/api/gallery/my-votes?captureIds=${ids}`
          );
          if (votesResponse.ok) {
            const votesData = await votesResponse.json();
            setUserVotes(votesData.votes || {});
          }
        }
      } catch {
        // silently fail
      } finally {
        setLoaded(true);
      }
    };

    fetchPreview();
  }, [props.state.isAuthenticated]);

  const handleVote = async (captureId: number, voteType: string) => {
    if (!props.state.isAuthenticated) return;

    const result = await props.postCommands.voteOnGalleryItem(captureId, voteType);
    if (result && result.success) {
      setUserVotes((prev) => {
        const updated = { ...prev };
        if (result.voteType === null) {
          delete updated[captureId];
        } else {
          updated[captureId] = result.voteType;
        }
        return updated;
      });

      setItems((prev) =>
        prev.map((item) => {
          if (item.Id !== captureId) return item;
          const updated = { ...item };
          if (result.action === "added") {
            updated[voteType === "like" ? "like_count" : "dislike_count"] =
              (updated[voteType === "like" ? "like_count" : "dislike_count"] || 0) + 1;
          } else if (result.action === "removed") {
            updated[voteType === "like" ? "like_count" : "dislike_count"] = Math.max(
              (updated[voteType === "like" ? "like_count" : "dislike_count"] || 1) - 1,
              0
            );
          } else if (result.action === "switched") {
            const addKey = voteType === "like" ? "like_count" : "dislike_count";
            const removeKey = voteType === "like" ? "dislike_count" : "like_count";
            updated[addKey] = (updated[addKey] || 0) + 1;
            updated[removeKey] = Math.max((updated[removeKey] || 1) - 1, 0);
          }
          return updated;
        })
      );
    }
  };

  if (!loaded || items.length === 0) return null;

  return (
    <Box
      id="community-preview"
      sx={{
        px: 3,
        py: 4,
        borderTop: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
      }}
    >
      <Box
        id="community-preview-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography id="community-preview-title" variant="h5" sx={{ fontWeight: 700 }}>
          {t("community.homePreviewTitle")}
        </Typography>
        <Button
          id="community-preview-view-all"
          component="a"
          href="/Community"
          variant="outlined"
          size="small"
          sx={{ textTransform: "none" }}
        >
          {t("community.viewAll")}
        </Button>
      </Box>
      <Box
        id="community-preview-grid"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {items.map((item) => (
          <GalleryCard
            key={item.Id}
            item={item}
            userVote={userVotes[item.Id] || null}
            onVote={handleVote}
            isAuthenticated={props.state.isAuthenticated}
            onClick={(id) => setViewingCaptureId(id)}
          />
        ))}
      </Box>

      {viewingCaptureId && (
        <GalleryViewPopup
          captureId={viewingCaptureId}
          onClose={() => setViewingCaptureId(null)}
          userVote={userVotes[viewingCaptureId] || null}
          onVote={handleVote}
          isAuthenticated={props.state.isAuthenticated}
          currentUserId={props.state.user?.id}
          postCommands={props.postCommands}
        />
      )}
    </Box>
  );
};

export default CommunityPreview;
